import {
  Box,
  Button,
  Flex,
  Img,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Portal,
  useDisclosure,
  useToast
} from "@chakra-ui/react"
import { useAtom } from "jotai"
import React, { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import userProfile from "../../assets/images/userProfile.svg"
import { Paths } from "../../common/commonConstants"
import useErrorToast from "../../common/commonHooks/errorToast"
import useSuccessToast from "../../common/commonHooks/successToast"
import { Login } from "../../pages/login/login"
import { UserProfile } from "../../pages/login/userProfile/userProfile.main"
import { useLogoutUserMutation } from "../../type-and-hooks"
import ChevronThinIcon from "../commons/buttons/Button.support.chevron.thin"
import { userDetailsAtom } from "./menu.store"
import { logout } from "./menu.support"

interface MenuFMPButtonProps {
  onNavigate: () => void
  ml?: string | number | Array<string | number>
}
export const MenuFMPButton: React.FC<MenuFMPButtonProps> = ({ onNavigate, ml }): JSX.Element => {
  const navigate = useNavigate()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [showUserProfile, setShowUserProfile] = useState<boolean>(true)
  const [userDetails, setUserDetails] = useAtom(userDetailsAtom)
  const [logoutAPICall, { loading: logoutLoading, data: logoutSuccess, error: logoutError }] = useLogoutUserMutation()
  useSuccessToast({
    toast,
    success: logoutSuccess,
    successMessage: "Successfully logged out"
  })
  useErrorToast({
    toast,
    error: logoutError,
    errorMessage: "Failed to Log Out"
  })

  const navigateToViewFMP = useCallback((): void => {
    navigate(Paths.FMP.MY_FMP)
  }, [navigate])
  const handleMyFMPClicked = (): void => {
    onNavigate?.()
    navigateToViewFMP()
  }
  const clearSessionAndRedirect = useCallback(() => {
    setUserDetails(null)
    logout(navigate)
    onClose()
  }, [navigate, setUserDetails])
  const handleLogout = (): void => {
    if (userDetails?._id) {
      logoutAPICall({
        variables: {
          userId: userDetails?._id
        }
      })
        .then(clearSessionAndRedirect)
        .catch(clearSessionAndRedirect)
    }
  }
  const handleLogin = useCallback(() => {
    setShowUserProfile(false)
    onOpen()
  }, [onOpen, onNavigate])

  return (
    <>
      {!userDetails?._id && (
        <Button variant="loginButton" onClick={handleLogin} mb="28px" ml={ml ?? "22px"}>
          Log In
        </Button>
      )}
      {userDetails && (
        <Flex flexDir={"column"} align="flex-end" ml={ml ?? [0, 0, 0, "22px"]} mb="28px">
          <Flex alignItems="center" mb="6px">
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                _hover={{ bg: "transparent" }}
                bg={"transparent"}
                p="0"
                m="0"
                h="auto"
                alignItems="center"
                justifyContent="center"
                color="white"
                fontSize="12px"
                display="flex"
                flexDirection="row"
                flexWrap="nowrap"
              >
                <Flex alignItems="center" flexDirection="row" flexWrap="nowrap">
                  {userDetails.first_name ?? "Guest"}
                  <Box ml="8px">
                    <ChevronThinIcon color="white" strokeWidth={1.5} />
                  </Box>
                  <Img src={userProfile} w="20.19px" ml="12px" alt="User Profile" />
                </Flex>
              </MenuButton>
              <MenuList zIndex="1000" bg="primary" boxShadow={"md"}>
                <MenuItem
                  borderRadius="0"
                  borderBottom="1px solid accent"
                  w="100%"
                  bg="primary"
                  color="white"
                  _hover={{ bg: "white", color: "primary" }}
                  textAlign={"left"}
                  justifyContent={"flex-start"}
                  fontWeight={"normal"}
                  onClick={() => {
                    setShowUserProfile(true)
                    onOpen()
                  }}
                >
                  User Settings
                </MenuItem>
                <MenuItem
                  borderRadius="0"
                  borderBottom="1px solid accent"
                  w="100%"
                  bg="primary"
                  color="white"
                  _hover={{ bg: "white", color: "primary" }}
                  textAlign={"left"}
                  justifyContent={"flex-start"}
                  fontWeight={"normal"}
                  isDisabled={!userDetails?._id}
                  onClick={handleLogout}
                >
                  {logoutLoading ? "Logging Out..." : "Logout"}
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
          <Flex maxWidth={"100px"}>
            <Button
              bg="white"
              minWidth="100px"
              py="0"
              minH="30px"
              h="30px"
              borderRadius="full"
              px="10px"
              textAlign="left"
              fontWeight="normal"
              fontSize="14px"
              color="primary"
              display="flex"
              w="100%"
              fontFamily="Work sans"
              onClick={handleMyFMPClicked}
            >
              My FMPs
            </Button>
          </Flex>
        </Flex>
      )}
      <Portal>
        <Modal isOpen={isOpen} onClose={onClose} size={"full"}>
          <ModalContent>
            {showUserProfile && <ModalCloseButton color="white" />}

            <ModalBody padding="0" bg="primary">
              {!showUserProfile && (
                <Login
                  successCallBack={() => {
                    onClose()
                    navigate(Paths.FMP.MY_FMP)
                  }}
                  loginCancelCallBack={() => {
                    onClose()
                  }}
                />
              )}
              {showUserProfile && <UserProfile onBackCallback={onClose} />}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Portal>
    </>
  )
}
